import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

/**
 * Centralized confirm dialog using shadcn AlertDialog.
 * Replaces window.confirm() with a styled, app-consistent dialog.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Elimina articolo',
 *     description: 'Stai per eliminare definitivamente "Posa piastrelle".',
 *     confirmText: 'Elimina',
 *     cancelText: 'Annulla',
 *     destructive: true,
 *   });
 *   if (!ok) return;
 */
const ConfirmContext = createContext(null);

const defaultOptions = {
  title: 'Conferma',
  description: 'Sei sicuro di voler procedere?',
  confirmText: 'Conferma',
  cancelText: 'Annulla',
  destructive: false,
};

export const ConfirmProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(defaultOptions);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setOptions({ ...defaultOptions, ...opts });
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const finish = (result) => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={open}
        onOpenChange={(o) => { if (!o) finish(false); }}
      >
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {options.destructive && (
                <AlertTriangle className="h-5 w-5 text-destructive" />
              )}
              {options.title}
            </AlertDialogTitle>
            {options.description && (
              <AlertDialogDescription>
                {options.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => finish(false)}
              data-testid="confirm-dialog-cancel"
            >
              {options.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => finish(true)}
              className={options.destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
              data-testid="confirm-dialog-confirm"
            >
              {options.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback to window.confirm if provider not mounted (safety net)
    return (opts) => Promise.resolve(window.confirm(opts?.description || opts?.title || 'Confermare?'));
  }
  return ctx;
};
